import { router } from "@inertiajs/react";
import { Autocomplete, Button, Select } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconPlus } from "@tabler/icons-react";
import type { DataTableSortStatus } from "mantine-datatable";
import { useState } from "react";
import { FilterSection } from "../../components/filter/FilterSection";
import { GroupTable } from "../../components/group/GroupTable";
import { showDeleteModal } from "../../helpers/modal";
import { getFullName } from "../../helpers/stringHelper";
import { useYearContext } from "../../hooks/useYearContext";
import { ContentShell } from "../../layout/ContentShell";
import type { Coach } from "../../models/coach";
import { getOccupationOptions, type Occupation } from "../../models/enums/occupation";
import type { Group } from "../../models/group";
import type { Pagination } from "../../models/pagination";
import { getYearContextOptions, type YearContext } from "../../models/yearContext";
import { edit_group_path, group_path, groups_path, new_group_path } from "../../routes";

type GroupsProps = {
  groups: Group[];
  group_names: string[];
  coaches: Coach[];
  year_contexts: YearContext[];
  filter: FilterFormType & {
    sort_key?: "name" | "responsible_coach" | "year_context" | "occupation";
    sort_direction?: "asc" | "desc";
  };
  pagination: Pagination;
};

type FilterFormType = {
  year_context_id?: string;
  coach_id?: string;
  occupation?: Occupation;
  name?: string;
};

const resetFilters: FilterFormType = {
  year_context_id: undefined,
  coach_id: undefined,
  occupation: undefined,
  name: undefined,
};

export default function Groups({ groups, filter, year_contexts, coaches, group_names, pagination }: GroupsProps) {
  const { pathWithYearContext } = useYearContext();
  const { sort_key, sort_direction, ...filterFormData } = filter;

  const sortStatus: DataTableSortStatus<Group> = {
    columnAccessor: sort_key || "",
    direction: sort_direction || "asc",
  };
  const [isFetching, setIsFetching] = useState(false);

  const coachOptions = coaches.map((coach) => ({
    value: coach.id.toString(),
    label: getFullName(coach),
  }));

  const filterForm = useForm<FilterFormType>({
    mode: "uncontrolled",
    initialValues: filterFormData,
  });

  const onSortStatusChange = (sortStatus: DataTableSortStatus<Group>) => handlePageUpdate(filterFormData, sortStatus, pagination.page);
  const onFilterSubmit = () => handlePageUpdate(filterForm.getValues(), sortStatus, pagination.page);
  const onReset = () => handlePageUpdate(resetFilters, sortStatus, pagination.page);
  const onPageChange = (page: number) => handlePageUpdate(filterFormData, sortStatus, page);

  const handlePageUpdate = (filters: FilterFormType, sortStatus: DataTableSortStatus<Group>, page: number) => {
    setIsFetching(true);
    router.visit(pathWithYearContext(groups_path), {
      only: ["groups", "filter", "pagination"],
      data: {
        filter: {
          ...filters,
          sort_key: sortStatus.columnAccessor,
          sort_direction: sortStatus.direction,
        },
        page,
      },
      onFinish: () => setIsFetching(false),
    });
  };

  const onNew = () => router.get(pathWithYearContext(new_group_path));
  const onEdit = (group: Group) => router.get(pathWithYearContext(edit_group_path, group.id));
  const onView = (group: Group) => router.get(pathWithYearContext(group_path, group.id));
  const onDelete = (group: Group) =>
    showDeleteModal({
      title: `Gruppe ${group.name}`,
      article: "die",
      onConfirm: () => router.delete(pathWithYearContext(group_path, group.id)),
    });

  return (
    <ContentShell
      title="Gruppen"
      actions={
        <Button leftSection={<IconPlus />} variant="light" onClick={onNew}>
          Neu
        </Button>
      }
    >
      <FilterSection
        form={filterForm}
        onSubmit={onFilterSubmit}
        onReset={onReset}
        filters={[
          <Autocomplete data={group_names} label="Gruppe" key={filterForm.key("name")} {...filterForm.getInputProps("name")} />,
          <Select data={coachOptions} label="Coach" clearable key={filterForm.key("coach_id")} {...filterForm.getInputProps("coach_id")} />,
          <Select
            data={getYearContextOptions(year_contexts)}
            label="Jahrgang"
            clearable
            key={filterForm.key("year_context_id")}
            {...filterForm.getInputProps("year_context_id")}
          />,
          <Select
            data={getOccupationOptions()}
            label="Beruf"
            clearable
            key={filterForm.key("occupation")}
            {...filterForm.getInputProps("occupation")}
          />,
        ]}
      />

      <GroupTable
        groups={groups}
        sortStatus={sortStatus}
        onSortStatusChange={onSortStatusChange}
        onDelete={onDelete}
        onEdit={onEdit}
        onView={onView}
        pagination={pagination}
        onPageChange={onPageChange}
        fetching={isFetching}
      />
    </ContentShell>
  );
}
